import React, { useState } from 'react';
import Snackbar from '../../Snackbar/Snackbar';
import { useHasAudioInputDevices, useHasVideoInputDevices } from '../../../hooks/deviceHooks/deviceHooks';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

export function getSnackbarContent(hasAudio: boolean, hasVideo: boolean, error?: Error) {
  let headline = '';
  let message = '';

  switch (true) {
    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === 'NotAllowedError':
      headline = 'Incapaz de acessar as mídias:';

      if (error!.message === 'Permission denied by system') {
        // Chrome only
        message =
          'O sistema operacional bloqueou o acesso do navegador ao microfone ou câmera. Verifique as configurações do seu sistema operacional.';
      } else {
        message =
          'O usuário negou permissão para usar áudio e vídeo. Conceda permissão ao navegador para acessar o microfone e a câmera.';
      }

      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === 'NotFoundError':
      headline = 'Não é possível encontrar microfone ou câmera:';
      message =
        'O navegador não pode acessar o microfone ou a câmera. Certifique-se de que todos os dispositivos de entrada estejam conectados e habilitados.';
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headline = 'Erro ao adquirir mídia:';
      message = `${error!.name} ${error!.message}`;
      break;

    case !hasAudio && !hasVideo:
      headline = 'Nenhuma câmera ou microfone detectado:';
      message = 'Os outros participantes na sala não poderão ver e ouvir você.';
      break;

    case !hasVideo:
      headline = 'Nenhuma câmera detectada:';
      message = 'Os outros participantes na sala não poderão ver você.';
      break;

    case !hasAudio:
      headline = 'Nenhum microfone detectado:';
      message = 'Os outros participantes na sala não poderão ouvir você.';
  }

  return {
    headline,
    message,
  };
}

export default function MediaErrorSnackbar({ error }: { error?: Error }) {
  const hasAudio = useHasAudioInputDevices();
  const hasVideo = useHasVideoInputDevices();

  const { isAcquiringLocalTracks } = useVideoContext();

  const [isSnackbarDismissed, setIsSnackbarDismissed] = useState(false);

  const isSnackbarOpen = !isSnackbarDismissed && !isAcquiringLocalTracks && (Boolean(error) || !hasAudio || !hasVideo);

  const { headline, message } = getSnackbarContent(hasAudio, hasVideo, error);

  return (
    <Snackbar
      open={isSnackbarOpen}
      handleClose={() => setIsSnackbarDismissed(true)}
      headline={headline}
      message={message}
      variant="warning"
    />
  );
}
